/**
 * View 1, Talent profile / 9-box: impact by potential across everyone placed,
 * the people in any box a click away. Shares in the grid are of the people
 * placed on it; the tiles say how many in scope are not placed yet and why.
 */
import React, { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import type { ReportingPerson } from '@/services/api/talentReportingService'
import {
  PERFORMANCE_SHORT,
  POTENTIAL_SHORT,
  ZONE_CELL_CLASS,
  ZONE_TEXT_CLASS,
  eduvosBlock,
  zoneOf,
  type Level,
} from '@/components/performance/manager/talent-review/eduvosBlocks'
import { Panel, PeopleTable, Tile, TileRow, type PeopleColumn } from './TalentDashboardParts'
import { blockCounts, count, fmtPct, roleOf, scopeSummary } from './talentDashboardData'

const IMPACT_COLUMNS: Level[] = ['low', 'medium', 'high']
const POTENTIAL_ROWS: Level[] = ['high', 'medium', 'low']

export const NINE_BOX_COLUMNS: PeopleColumn[] = [
  { key: 'name', label: 'Name', render: (p) => p.name, sortValue: (p) => p.name },
  { key: 'role', label: 'Role', render: (p) => roleOf(p) || '', sortValue: (p) => roleOf(p) || '' },
  { key: 'function', label: 'Function', render: (p) => p.function || '', sortValue: (p) => p.function || '' },
  { key: 'department', label: 'Department', render: (p) => p.department || '', sortValue: (p) => p.department || '' },
  { key: 'team_1', label: 'Team', render: (p) => p.team_1 || '', sortValue: (p) => p.team_1 || '' },
  { key: 'layer', label: 'Leadership layer', render: (p) => p.leadership_layer || '', sortValue: (p) => p.leadership_layer || '' },
  { key: 'impact', label: 'Impact', render: (p) => (p.performance ? PERFORMANCE_SHORT[p.performance] : ''), sortValue: (p) => p.performance || '' },
  { key: 'potential', label: 'Potential', render: (p) => (p.potential ? POTENTIAL_SHORT[p.potential] : ''), sortValue: (p) => p.potential || '' },
  { key: 'box', label: 'Box', render: (p) => p.block ?? '', sortValue: (p) => p.block ?? 99, className: 'text-center' },
  { key: 'hipo', label: 'HIPO', render: (p) => (p.is_hipo ? 'Yes' : ''), sortValue: (p) => (p.is_hipo ? 0 : 1) },
]

export const NineBoxView: React.FC<{
  people: ReportingPerson[]
  onExport?: (rows: ReportingPerson[], what: string) => void
}> = ({ people, onExport }) => {
  const [box, setBox] = useState<number | null>(null)
  const summary = useMemo(() => scopeSummary(people), [people])
  const counts = useMemo(() => blockCounts(people), [people])
  const placed = useMemo(() => people.filter((p) => p.block !== null), [people])
  const shown = useMemo(() => (box ? placed.filter((p) => p.block === box) : placed), [placed, box])
  const notPlaced = summary.inScope - summary.placed
  const hipoByBox = [1, 2, 3].map((b) => `Box ${b}: ${counts[b]}`).join(' · ')
  const others = [4, 5, 6, 7, 8, 9].reduce((n, b) => n + counts[b], 0)

  return (
    <div className="space-y-4">
      <TileRow>
        <Tile label="Employees in scope" value={summary.inScope} sub="Permanent and fixed-term, interns included" />
        <Tile label="Placed on the grid" value={summary.placed} sub={`${fmtPct(summary.placed, summary.inScope)} of those in scope`} />
        <Tile label="High-potentials" value={summary.hipos} sub={`Boxes 1, 2 and 3 · ${fmtPct(summary.hipos, summary.placed)} of those placed`} />
        <Tile
          label="Not placed yet"
          value={notPlaced}
          tone={notPlaced ? 'warn' : 'default'}
          sub={summary.notPlaced.map((r) => `${r.count} ${r.phrase}`).join(' · ') || 'Everyone in scope is placed'}
        />
      </TileRow>

      <Panel
        title="Impact by potential"
        note={`Share of the ${count(summary.placed, 'person', 'people')} placed. High-potentials, ${hipoByBox}; the other boxes hold ${others}. Click a box to list its people.`}
      >
        <div className="grid grid-cols-[4.5rem_repeat(3,minmax(0,1fr))] gap-2" aria-label="Impact by potential">
          {POTENTIAL_ROWS.map((pot) => (
            <React.Fragment key={pot}>
              <div className="flex items-center justify-end text-right text-[11px] font-semibold text-gray-500">
                {POTENTIAL_SHORT[pot]}
              </div>
              {IMPACT_COLUMNS.map((perf) => {
                const block = eduvosBlock(perf, pot)!
                const zone = zoneOf(block.number)!
                const n = counts[block.number]
                const selected = box === block.number
                return (
                  <button
                    key={`${perf}-${pot}`}
                    type="button"
                    aria-pressed={selected}
                    aria-label={`Box ${block.number}, ${block.label}: ${count(n, 'person', 'people')}`}
                    onClick={() => setBox(selected ? null : block.number)}
                    className={cn(
                      'rounded-lg border p-3 text-left transition-shadow hover:shadow-sm',
                      ZONE_CELL_CLASS[zone],
                      selected && 'ring-2 ring-heritage-blue',
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-lg font-bold text-heritage-blue">{block.number}</span>
                      <span className="tabular-nums text-xl font-bold text-gray-800">{n}</span>
                    </div>
                    <div className="mt-0.5 flex items-baseline justify-between gap-2">
                      <span className={cn('text-[11px] leading-tight', ZONE_TEXT_CLASS[zone])}>{block.label}</span>
                      <span className="tabular-nums text-[11px] text-gray-500">{fmtPct(n, summary.placed)}</span>
                    </div>
                  </button>
                )
              })}
            </React.Fragment>
          ))}
          <div className="text-right text-[10px] uppercase tracking-wide text-gray-400">Potential / Impact</div>
          {IMPACT_COLUMNS.map((perf) => (
            <div key={perf} className="text-center text-[11px] font-semibold text-gray-500">
              {PERFORMANCE_SHORT[perf]}
            </div>
          ))}
        </div>
      </Panel>

      <PeopleTable
        title={box ? `People in box ${box}` : 'People placed'}
        people={shown}
        columns={NINE_BOX_COLUMNS}
        onExport={onExport && (() => onExport(shown, box ? `Box ${box}` : 'Placed'))}
      />
    </div>
  )
}
